/**
 * Navigation Repository
 * COMPLETE implementation for navigation data
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Position, Passage, Waypoint, PortCall } from '../../../types/database';

export class PositionRepository extends BaseRepository<Position> {
  constructor() {
    super(db.positions, 'positions');
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<Position[]> {
    return await this.table
      .where('timestamp')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async getLatest(limit: number = 1): Promise<Position[]> {
    const positions = await this.table.toArray();
    return positions
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, limit);
  }

  async findByShip(shipId: string): Promise<Position[]> {
    return await this.table.where('vesselId').equals(shipId).toArray();
  }
}

export class PassageRepository extends BaseRepository<Passage> {
  constructor() {
    super(db.passages, 'passages');
  }

  async findByStatus(status: Passage['status']): Promise<Passage[]> {
    if (!status) return [];
    return await this.table.where('status').equals(status).toArray();
  }

  async findByPort(portName: string, type: 'departure' | 'arrival'): Promise<Passage[]> {
    return await this.table.filter(p =>
      type === 'departure' ? p.departurePort === portName : p.arrivalPort === portName
    ).toArray();
  }

  async getActive(): Promise<Passage[]> {
    return await this.findByStatus('in_progress');
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<Passage[]> {
    return await this.table
      .where('plannedDeparture')
      .between(startDate, endDate, true, true)
      .toArray();
  }
}

export class WaypointRepository extends BaseRepository<Waypoint> {
  constructor() {
    super(db.waypoints, 'waypoints');
  }

  async findByPassage(passageId: string): Promise<Waypoint[]> {
    const waypoints = await this.table
      .where('passageId')
      .equals(passageId)
      .toArray();
    
    return waypoints.sort((a, b) => (a.waypointNumber || 0) - (b.waypointNumber || 0));
  }
}

export class PortCallRepository extends BaseRepository<PortCall> {
  constructor() {
    super(db.portCalls, 'portCalls');
  }

  async findByStatus(status: PortCall['status']): Promise<PortCall[]> {
    if (!status) return [];
    return await this.table.where('status').equals(status).toArray();
  }

  async findByPort(portName: string): Promise<PortCall[]> {
    return await this.table.where('portName').equals(portName).toArray();
  }

  async findUpcoming(days: number = 30): Promise<PortCall[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    return await this.table
      .where('plannedArrival')
      .between(now, futureDate, true, true)
      .filter(pc => pc.status === 'planned')
      .toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<PortCall[]> {
    return await this.table
      .where('plannedArrival')
      .between(startDate, endDate, true, true)
      .toArray();
  }
}

export const positionRepository = new PositionRepository();
export const passageRepository = new PassageRepository();
export const waypointRepository = new WaypointRepository();
export const portCallRepository = new PortCallRepository();


